import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  AppBar,
  AppBarProps,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import NextLink from "next/link";
import { useRouter } from "next/router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import showdown from "showdown";
import { AddPaymentModal } from "src/components/dashboard/addPaymenModal";
import { getArticle } from "src/slices/articles/thunks";
import {
  selectIsHumanized,
  selectIsHumanizing,
  selectIsHumanizingDone,
  selectShowHumanizeContent,
  selectShowOriginalContent,
  setIsCopyContent,
  setIsDownloadContent,
  setIsHumanized,
  setShowHumanizeContent,
} from "src/slices/humanize";
import { startHumanization } from "src/slices/humanize/thunks";
import PresenceStack from "src/components/dashboard/articleEditor/PresenceStack";
import { useArticlePresenceContext } from "src/components/dashboard/articleEditor/ArticlePresenceContext";
import { getOptimizedArticle } from "src/slices/optimizeContent/thunks";
import { tabSelected } from "src/slices/system";
import { TabType } from "src/slices/system/types";
import { useDispatch } from "src/store";

interface EditorNavbarProps extends AppBarProps {
  onOpenSidebar: () => void;
  title: string;
  description: string;
  id: string;
  isHideArticle: string;
  isOptimizedEditor?: boolean;
  isEEATMode?: boolean;
}

const EditorNavbarRoot = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...(theme.palette.mode === "dark"
    ? {
        boxShadow: theme.shadows[3],
      }
    : {
        backgroundColor: theme.palette.background.paper,
        borderBottomColor: theme.palette.divider,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        boxShadow: "none",
      }),
}));

export const EditorNavbar: FC<EditorNavbarProps> = (props) => {
  const { onOpenSidebar, ...other } = props;

  const dispatch = useDispatch();
  const presence = useArticlePresenceContext();

  const isHumanizing = useSelector(selectIsHumanizing);

  const isHumanizingDone = useSelector(selectIsHumanizingDone);

  const isHumanized = useSelector(selectIsHumanized);

  const showHumanizeContent = useSelector(selectShowHumanizeContent);
  const showOriginalContent = useSelector(selectShowOriginalContent);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [humanizeToggletext, setHumanizeToggletext] = useState(
    `Once your article is generated, our system proofreads, rewrites, and adjusts the text using AI to sound more natural and human-like, helping it avoid AI detection.
    <br /><br />
    Note: Headings and structure may change to improve readability. This is an automated process.`
  );

  const handleChange = (event: React.ChangeEvent<{}>, value: TabType): void => {
    dispatch(tabSelected(value));
  };

  const converter = new showdown.Converter();

  const [offset, setOffset] = useState(0);
  const [stickedToTop, setStickedToTop] = useState(false);
  const [html, setHtml] = useState("");
  const [humanizedHtml, setHumanizedHtml] = useState("");
  const [editorContent, setEditorContent] = useState<string>("");

  const router = useRouter();

  useEffect(() => {
    if (offset > 16 && !stickedToTop) {
      setStickedToTop(true);
    }
    if (offset == 0) {
      setStickedToTop(false);
    }
  }, [offset]);

  const handleBack = () => {
    // Check if we have a referrer or history
    const referrer = document.referrer;

    if (referrer && referrer.includes("/brief")) {
      // If coming from a brief page, go to that specific brief
      router.push(referrer);
    } else if (window.history.length > 1) {
      // Use browser history if available
      router.back();
    } else if (props.isOptimizedEditor) {
      // Default fallback to dashboard
      router.push("/contentUpdate");
    } else {
      // Default fallback to dashboard
      router.push("/dashboard");
    }
  };
  const addMetaToArticle = (html: string) => {
    html = `
    <p><strong>meta title:</strong> ${props.title}</p>
    <p><strong>meta description:</strong> ${props.description}</p>
    ${html}
    `;
    return html;
  };
  const handleCopy = () => {
    dispatch(setIsCopyContent(true));
  };

  const handleDownload = () => {
    dispatch(setIsDownloadContent(true));
  };

  const handleHumanizeClick = () => {
    if (props.isHideArticle === "true") {
      setShowPaymentModal(true);
      return;
    }
    dispatch(startHumanization());
  };

  const asyncGetArticle = async () => {
    let html = "";
    let humanizedHtml = "";
    let isHumanized = false;
    if (!props.isOptimizedEditor) {
      const payload = await dispatch(getArticle({ id: props.id })).unwrap();
      html = converter.makeHtml(payload?.data?.content);
      humanizedHtml = converter.makeHtml(payload?.data?.humanizedContent);
      isHumanized = payload?.data?.isHumanized;
    } else {
      const payload = await dispatch(
        getOptimizedArticle({ id: props.id })
      ).unwrap();
      html = converter.makeHtml(payload?.data?.content);
      humanizedHtml = converter.makeHtml(payload?.data?.humanizedContent);
      isHumanized = payload?.data?.isHumanized;
    }

    // Set the HTML content first
    setHtml(html);
    setHumanizedHtml(humanizedHtml);

    // Set the initial editor content based on isHumanized state
    setEditorContent(isHumanized ? humanizedHtml : html);
    dispatch(setIsHumanized(isHumanized));
  };
  useEffect(() => {
    asyncGetArticle();
  }, [props.id]);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    // Only update the display preference, not the document state
    dispatch(setShowHumanizeContent(isChecked));
  };
  useEffect(() => {
    if (isHumanized === true) {
      dispatch(setShowHumanizeContent(true));
    } else {
      dispatch(setShowHumanizeContent(false));
    }
  }, [isHumanized]);

  return (
    <>
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          dispatch(setIsDownloadContent(false));
          dispatch(setIsCopyContent(false));
        }}
        location={""}
      />

      <Box
        sx={{
          // paddingBottom: "16px",
          paddingLeft: "0px",
          paddingRight: "0px",
          // marginTop: "16px",
          position: "sticky",
          width: "100%",
          zIndex: 99999,
          top: 0,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <EditorNavbarRoot
          {...other}
          sx={{
            // borderRadius: stickedToTop ? "0rem" : "0.5rem",
            position: "static",
            //   backgroundColor: "#E8E8E8",
            backgroundColor: "#080548",
            height: "50px",
            paddingLeft: stickedToTop ? "16px" : "0px",
            paddingRight: stickedToTop ? "16px" : "0px",
            transition: "all 0.3s ease-in-out",
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              left: 0,
              px: 2,
              height: "50px",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              justifyContent="space-between"
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: "1rem" }}>
                <NextLink href="/dashboard" passHref>
                  <img
                    src="/app/favicon-32x32.png"
                    alt="Logo"
                    style={{
                      height: "30px",
                      width: "auto",
                      cursor: "pointer",
                    }}
                  />
                </NextLink>
                {!props.isEEATMode && (
                  <Box sx={{ ml: "1rem" }}>
                    {/* <Tooltip title="Back to Brief" placement="top"> */}
                    <IconButton onClick={handleBack}>
                      <ArrowBackIcon />
                    </IconButton>
                    {/* </Tooltip> */}
                  </Box>
                )}
                {!props.isEEATMode && (
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontWeight: "400",
                      lineHeight: "24px",
                      letterSpacing: "0.01em",
                      textAlign: "left",
                      color: "#fff",
                      ml: "4.5rem",
                    }}
                  >
                    Content Editor / {props.title}
                  </Typography>
                )}
              </Box>
              {!props.isEEATMode && (
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: "1rem" }}>
                  {presence.active && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "1rem" }}>
                      <PresenceStack
                        others={presence.others}
                        me={presence.me}
                        onRename={presence.setDisplayName}
                        onDark
                        holderSessionId={presence.holder?.sessionId}
                        iAmHolder={presence.isHolder}
                      />
                      {!presence.canEdit && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            // Quiet, passive status tag — deliberately NOT button-like
                            // (flat, translucent, no pointer) so only the red Take Over
                            // button reads as clickable.
                            backgroundColor: "rgba(255,255,255,0.12)",
                            color: "rgba(255,255,255,0.85)",
                            fontSize: "11px",
                            fontWeight: 500,
                            lineHeight: "16px",
                            borderRadius: "6px",
                            py: "2px",
                            px: "8px",
                            whiteSpace: "nowrap",
                            cursor: "default",
                            userSelect: "none",
                          }}
                        >
                          🔒 Read only
                        </Box>
                      )}
                      {presence.holder && !presence.isHolder ? (
                        <Button
                          onClick={() => {
                            // Fire-and-forget: requestTakeOver fails closed (no
                            // control granted on backend failure) and surfaces its
                            // own error snack — swallow the rejection here so it
                            // doesn't bubble as an unhandled promise rejection.
                            void presence.requestTakeOver().catch(() => {});
                          }}
                          variant="contained"
                          sx={{
                            color: "#fff",
                            backgroundColor: "#DC2626",
                            fontSize: "11px",
                            lineHeight: "16px",
                            py: "2px",
                            px: "10px",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            // Raised vs the flat status tag so it clearly reads as the
                            // (only) clickable control.
                            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                            "&:hover": {
                              backgroundColor: "#B91C1C",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                            },
                          }}
                        >
                          ⇄ Take over{presence.holder.kind === "agent" ? " from 🤖" : ""}
                        </Button>
                      ) : presence.isHolder ? (
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "#22C55E",
                            }}
                          />
                          <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.85)" }}>
                            Editing
                          </Typography>
                        </Stack>
                      ) : null}
                    </Box>
                  )}
                  {isHumanized === true ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center", mr: "1rem" }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "20px",
                          letterSpacing: "0.01em",
                          textAlign: "left",
                          color: "#fff",
                        }}
                      >
                        Original
                      </Typography>
                      <Switch
                        checked={showHumanizeContent}
                        onChange={handleToggle}
                        inputProps={{ "aria-label": "ant design" }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "20px",
                          letterSpacing: "0.01em",
                          textAlign: "left",
                          color: "#fff",
                        }}
                      >
                        Humanized
                      </Typography>
                    </Stack>
                  ) : (
                    <Tooltip
                      title={
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              humanizeToggletext ||
                              "We'll add your personal experience, know-how, data and quotes to enrich the AI article.",
                          }}
                        />
                      }
                      placement="top"
                      PopperProps={{
                        sx: {
                          zIndex: 100000,
                        },
                      }}
                    >
                      <span>
                      <Button
                        onClick={handleHumanizeClick}
                        variant="outlined"
                        disabled={isHumanizing || (presence.active && !presence.canEdit)}
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "20px",
                          letterSpacing: "0.01em",
                          textAlign: "left",
                          padding: "0.5rem 1.5rem",
                          mr: "1rem",
                          backgroundColor: "#fff",
                          "&:hover": {
                            color: "#080548",
                            borderColor: "#080548 !important",
                            backgroundColor: "#fff !important",
                          },
                        }}
                      >
                        {isHumanizing ? (
                          <CircularProgress
                            size={20}
                            sx={{
                              color: "#080548",
                            }}
                          />
                        ) : (
                          "Humanize"
                        )}
                      </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Button
                    onClick={handleCopy}
                    variant="contained"
                    sx={{
                      fontSize: "12px",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: "0.01em",
                      textAlign: "left",
                      mr: "1rem",
                      padding: "0.5rem 1.5rem",
                    }}
                  >
                    Copy
                  </Button>
                  <Button
                    onClick={handleDownload}
                    variant="contained"
                    sx={{
                      fontSize: "12px",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: "0.01em",
                      textAlign: "left",
                      padding: "0.5rem 1.5rem",
                    }}
                  >
                    Download
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </EditorNavbarRoot>
      </Box>
    </>
  );
};
